import { z } from 'zod';

import { reasonSchema } from './clinical-common.ts';
import { amountSchema, signedAmountSchema } from './money.ts';
import { transferLineBodySchema } from './stock-documents.ts';
import { stockQuantitySchema } from './stock.ts';

// Procurement on the wire (docs/plans/phase2-procurement.md §3): store requisitions,
// purchase requisitions, LPOs, GRN reversals, supplier invoices and supplier statements.
// Quantities are whole base units unless named `packs`. Money is integer cents in a string
// (ADR 0006). Calendar days are `YYYY-MM-DD`; instants are ISO strings in UTC.

const isoDay = z.iso.date();
const instant = z.iso.datetime({ offset: true });
const optionalNote = z.string().trim().max(500).optional();
const unitCode = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Lower-case letters, digits and _');
const person = z.object({ id: z.uuid(), name: z.string() });
const named = z.object({ id: z.uuid(), name: z.string() });
const listLimit = z.coerce.number().int().min(1).max(100).default(50);

const eachItemOnce = (lines: readonly { itemId: string }[]) =>
  new Set(lines.map((l) => l.itemId)).size === lines.length;

export const reasonBodySchema = z.object({ reason: reasonSchema });

// --- Store requisitions -------------------------------------------------------------------

export const STORE_REQUISITION_STATUSES = [
  'open',
  'partly_issued',
  'issued',
  'closed',
  'rejected',
  'cancelled',
] as const;

export const createStoreRequisitionBodySchema = z
  .object({
    clientRequestId: z.uuid(),
    requestingStoreId: z.uuid(),
    supplyingStoreId: z.uuid(),
    note: optionalNote,
    lines: z
      .array(z.object({ itemId: z.uuid(), quantity: stockQuantitySchema }))
      .min(1)
      .max(100),
  })
  .refine((r) => r.requestingStoreId !== r.supplyingStoreId, {
    message: 'Choose two different stores',
    path: ['supplyingStoreId'],
  })
  .refine((r) => eachItemOnce(r.lines), { message: 'List each item once', path: ['lines'] });

export const storeRequisitionListQuerySchema = z.object({
  storeId: z.uuid(),
  /** `from_store`: what this store asked for. `of_store`: what others asked this store for. */
  direction: z.enum(['from_store', 'of_store']),
  status: z.enum(STORE_REQUISITION_STATUSES).optional(),
  limit: listLimit,
});

export const issueStoreRequisitionBodySchema = z
  .object({
    clientRequestId: z.uuid(),
    note: optionalNote,
    lines: z.array(transferLineBodySchema).min(1).max(100),
  })
  .refine((r) => eachItemOnce(r.lines), { message: 'List each item once', path: ['lines'] });

export const storeRequisitionSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  requestingStore: named,
  supplyingStore: named,
  status: z.enum(STORE_REQUISITION_STATUSES),
  note: z.string().nullable(),
  requestedBy: person,
  requestedAt: instant,
  closedBy: person.nullable(),
  closedAt: instant.nullable(),
  closeReason: z.string().nullable(),
  lines: z.array(
    z.object({
      lineNo: z.number().int(),
      itemId: z.uuid(),
      itemName: z.string(),
      baseUnitDisplay: z.string(),
      requested: z.number().int(),
      issued: z.number().int(),
      outstanding: z.number().int(),
    }),
  ),
  issues: z.array(z.object({ transferId: z.uuid(), number: z.string(), transferredAt: instant })),
});

export type StoreRequisitionView = z.infer<typeof storeRequisitionSchema>;

// --- Purchase requisitions ----------------------------------------------------------------

export const PURCHASE_REQUISITION_STATUSES = [
  'requested',
  'approved',
  'rejected',
  'cancelled',
] as const;

export const createPurchaseRequisitionBodySchema = z
  .object({
    clientRequestId: z.uuid(),
    storeId: z.uuid(),
    note: optionalNote,
    lines: z
      .array(
        z.object({
          itemId: z.uuid(),
          packUnitCode: unitCode,
          packs: stockQuantitySchema,
          note: optionalNote,
        }),
      )
      .min(1)
      .max(100),
  })
  .refine((r) => eachItemOnce(r.lines), { message: 'List each item once', path: ['lines'] });

export const purchaseRequisitionListQuerySchema = z.object({
  status: z.enum(PURCHASE_REQUISITION_STATUSES).optional(),
  limit: listLimit,
});

const packLineFields = {
  lineNo: z.number().int(),
  itemId: z.uuid(),
  itemName: z.string(),
  baseUnitDisplay: z.string(),
  packUnitCode: z.string(),
  packUnitDisplay: z.string(),
  baseUnitsPerPack: z.number().int(),
  packs: z.number().int(),
  quantity: z.number().int(),
};

export const purchaseRequisitionSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  store: named,
  status: z.enum(PURCHASE_REQUISITION_STATUSES),
  note: z.string().nullable(),
  requestedBy: person,
  requestedAt: instant,
  decidedBy: person.nullable(),
  decidedAt: instant.nullable(),
  decisionReason: z.string().nullable(),
  lines: z.array(z.object({ ...packLineFields, note: z.string().nullable() })),
  orders: z.array(z.object({ id: z.uuid(), number: z.string(), status: z.string() })),
  /** True for someone holding procurement.approve who didn't ask for it. */
  canDecide: z.boolean(),
});

export type PurchaseRequisitionView = z.infer<typeof purchaseRequisitionSchema>;

// --- LPOs ---------------------------------------------------------------------------------

export const PURCHASE_ORDER_STATUSES = [
  'awaiting_approval',
  'approved',
  'rejected',
  'cancelled',
  'closed',
] as const;

export const RECEIPT_STATUS_LIST = [
  'not_received',
  'partly_received',
  'received',
  'over_received',
] as const;

export const createPurchaseOrderBodySchema = z
  .object({
    clientRequestId: z.uuid(),
    supplierId: z.uuid(),
    storeId: z.uuid(),
    purchaseRequisitionId: z.uuid().optional(),
    expectedOn: isoDay.optional(),
    note: optionalNote,
    lines: z
      .array(
        z.object({
          itemId: z.uuid(),
          packUnitCode: unitCode,
          packs: stockQuantitySchema,
          packPrice: amountSchema,
        }),
      )
      .min(1)
      .max(100),
  })
  .refine((r) => eachItemOnce(r.lines), { message: 'List each item once', path: ['lines'] });

export const purchaseOrderListQuerySchema = z.object({
  status: z.enum(PURCHASE_ORDER_STATUSES).optional(),
  supplierId: z.uuid().optional(),
  limit: listLimit,
  cursor: z.uuid().optional(),
});

export const purchaseOrderLineSchema = z.object({
  id: z.uuid(),
  ...packLineFields,
  packPrice: amountSchema,
  lineTotal: amountSchema,
  /** Base units on posted GRNs that haven't been reversed. */
  received: z.number().int(),
  outstanding: z.number().int(),
  /** Base units on supplier invoices that aren't rejected or cancelled. */
  invoiced: z.number().int(),
  receiptStatus: z.enum(RECEIPT_STATUS_LIST),
});

export const purchaseOrderSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  supplier: named,
  store: named,
  purchaseRequisition: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  status: z.enum(PURCHASE_ORDER_STATUSES),
  receiptStatus: z.enum(RECEIPT_STATUS_LIST),
  expectedOn: isoDay.nullable(),
  note: z.string().nullable(),
  total: amountSchema,
  submittedBy: person,
  submittedAt: instant,
  decidedBy: person.nullable(),
  decidedAt: instant.nullable(),
  decisionReason: z.string().nullable(),
  closedBy: person.nullable(),
  closedAt: instant.nullable(),
  closeReason: z.string().nullable(),
  lines: z.array(purchaseOrderLineSchema),
  receipts: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      storeId: z.uuid(),
      receivedAt: instant,
      status: z.enum(['draft', 'posted']),
      reversed: z.boolean(),
    }),
  ),
  invoices: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      supplierReference: z.string(),
      status: z.string(),
      total: amountSchema,
    }),
  ),
  canDecide: z.boolean(),
});

export type PurchaseOrderView = z.infer<typeof purchaseOrderSchema>;

export const purchaseOrderSummarySchema = purchaseOrderSchema
  .omit({ lines: true, receipts: true, invoices: true })
  .extend({ lineCount: z.number().int() });

export type PurchaseOrderSummary = z.infer<typeof purchaseOrderSummarySchema>;

// --- GRN reversals ------------------------------------------------------------------------

export const GRN_REVERSAL_STATUSES = ['requested', 'approved', 'rejected', 'cancelled'] as const;

export const grnReversalSchema = z.object({
  id: z.uuid(),
  goodsReceiptId: z.uuid(),
  grnNumber: z.string(),
  storeId: z.uuid(),
  status: z.enum(GRN_REVERSAL_STATUSES),
  reason: z.string(),
  requestedBy: person,
  requestedAt: instant,
  decidedBy: person.nullable(),
  decidedAt: instant.nullable(),
  decisionReason: z.string().nullable(),
  canDecide: z.boolean(),
});

export type GrnReversalView = z.infer<typeof grnReversalSchema>;

export const grnReversalListQuerySchema = z.object({
  storeId: z.uuid().optional(),
  status: z.enum(GRN_REVERSAL_STATUSES).optional(),
  limit: listLimit,
});

// --- Supplier invoices --------------------------------------------------------------------

export const SUPPLIER_INVOICE_STATUSES = [
  'awaiting_approval',
  'approved',
  'rejected',
  'cancelled',
] as const;

export const INVOICE_MATCH_FLAGS = [
  'price_above_order',
  'price_below_order',
  'quantity_above_received',
  'quantity_above_ordered',
] as const;

export const createSupplierInvoiceBodySchema = z
  .object({
    clientRequestId: z.uuid(),
    purchaseOrderId: z.uuid(),
    /** The invoice number as printed by the supplier. */
    supplierReference: z.string().trim().min(1).max(60),
    invoiceDate: isoDay,
    dueOn: isoDay.optional(),
    vat: amountSchema,
    total: amountSchema,
    note: optionalNote,
    lines: z
      .array(
        z.object({
          purchaseOrderLineId: z.uuid(),
          packs: stockQuantitySchema,
          packPrice: amountSchema,
        }),
      )
      .min(1)
      .max(100),
  })
  .refine((r) => new Set(r.lines.map((l) => l.purchaseOrderLineId)).size === r.lines.length, {
    message: 'List each LPO line once',
    path: ['lines'],
  });

export const supplierInvoiceListQuerySchema = z.object({
  status: z.enum(SUPPLIER_INVOICE_STATUSES).optional(),
  supplierId: z.uuid().optional(),
  purchaseOrderId: z.uuid().optional(),
  limit: listLimit,
});

export const approveSupplierInvoiceBodySchema = z.object({ reason: reasonSchema.optional() });

export const invoiceMatchSchema = z.object({
  matched: z.boolean(),
  totalMismatch: z.boolean(),
  linesTotal: amountSchema,
  expectedTotal: amountSchema,
  difference: signedAmountSchema,
  lines: z.array(
    z.object({
      lineNo: z.number().int(),
      purchaseOrderLineId: z.uuid(),
      itemName: z.string(),
      baseUnitDisplay: z.string(),
      packUnitDisplay: z.string(),
      baseUnitsPerPack: z.number().int(),
      packs: z.number().int(),
      packPrice: amountSchema,
      orderPackPrice: amountSchema,
      lineTotal: amountSchema,
      difference: signedAmountSchema,
      /** Base units: this line, ordered, received, and on other live invoices. */
      invoiced: z.number().int(),
      ordered: z.number().int(),
      received: z.number().int(),
      invoicedElsewhere: z.number().int(),
      flags: z.array(z.enum(INVOICE_MATCH_FLAGS)),
    }),
  ),
});

export type InvoiceMatchView = z.infer<typeof invoiceMatchSchema>;

export const supplierInvoiceSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  supplier: named,
  purchaseOrder: z.object({ id: z.uuid(), number: z.string() }),
  supplierReference: z.string(),
  invoiceDate: isoDay,
  dueOn: isoDay.nullable(),
  vat: amountSchema,
  total: amountSchema,
  status: z.enum(SUPPLIER_INVOICE_STATUSES),
  note: z.string().nullable(),
  recordedBy: person,
  recordedAt: instant,
  decidedBy: person.nullable(),
  decidedAt: instant.nullable(),
  decisionReason: z.string().nullable(),
  /** Live while awaiting approval; as the approver saw it once decided. */
  match: invoiceMatchSchema,
  matchAsOf: z.enum(['now', 'decision']),
  canDecide: z.boolean(),
});

export type SupplierInvoiceView = z.infer<typeof supplierInvoiceSchema>;

export const supplierInvoiceSummarySchema = supplierInvoiceSchema
  .omit({ match: true, matchAsOf: true })
  .extend({ matched: z.boolean() });

export type SupplierInvoiceSummary = z.infer<typeof supplierInvoiceSummarySchema>;

// --- Supplier statement -------------------------------------------------------------------

export const supplierStatementQuerySchema = z
  .object({ from: isoDay, to: isoDay })
  .refine((q) => q.from <= q.to, { message: 'The start is after the end', path: ['to'] });

export const supplierStatementSchema = z.object({
  supplier: z.object({ id: z.uuid(), name: z.string(), kraPin: z.string().nullable() }),
  from: isoDay,
  to: isoDay,
  /** Approved invoices dated before the period. */
  opening: amountSchema,
  rows: z.array(
    z.object({
      invoiceId: z.uuid(),
      number: z.string(),
      supplierReference: z.string(),
      invoiceDate: isoDay,
      orderNumber: z.string(),
      amount: amountSchema,
      balance: amountSchema,
    }),
  ),
  closing: amountSchema,
  awaitingApproval: z.array(
    z.object({
      invoiceId: z.uuid(),
      number: z.string(),
      supplierReference: z.string(),
      invoiceDate: isoDay,
      amount: amountSchema,
    }),
  ),
  /** Per LPO: goods received at the GRN cost, and what has been invoiced (approved or waiting). */
  receivedNotInvoiced: z.array(
    z.object({
      orderId: z.uuid(),
      orderNumber: z.string(),
      received: amountSchema,
      invoiced: amountSchema,
      difference: signedAmountSchema,
    }),
  ),
  openOrders: z.array(
    z.object({
      orderId: z.uuid(),
      number: z.string(),
      status: z.enum(PURCHASE_ORDER_STATUSES),
      receiptStatus: z.enum(RECEIPT_STATUS_LIST),
      total: amountSchema,
    }),
  ),
});

export type SupplierStatementView = z.infer<typeof supplierStatementSchema>;
